import { ChemistrySample } from "@/pages/caster/models/ElementData";
import { getMomentTz } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import {
	CellClassParams,
	CellClickedEvent,
	ColDef,
	ColGroupDef,
	ICellRendererParams,
	RowClassParams,
	RowStyle,
	ValueFormatterParams,
	ValueGetterParams,
} from "ag-grid-community";
import { useMemo } from "react";
import { CustomHeaderGroup } from "../components/CustomHeaderGroup";
import { StatusBarReference } from "../components/StatusBarReference";

interface Props {
	elementList: ChemistrySample[];
	coloringEnabled: boolean;
	onToggleColoring: (enabled: boolean) => void;
	editable?: boolean;
	onAddSample?: () => void;
	gradeId?: number | null;
	onDeleteSample?: (chemistrySampleId: number) => void;
	onCellClicked?: (params: CellClickedEvent) => void;
	isSampleTooOld?: (reportedDate: Date | null) => boolean;
}

const moment = getMomentTz();

// Transformed data structure for table rows
interface TransformedRowData {
	id: string;
	SampleTypeName: string | null;
	SampleSequence: number | null;
	Reception: Date | null;
	ChemistrySampleId?: number | null; // Store at row level for easier access
	[key: string]:
		| {
				value: number | null;
				status?: "Above" | "Below" | "Within" | "Info" | null;
				minValue?: number | null;
				maxValue?: number | null;
				decimals: number | null;
				chemistrySampleId?: number | null;
				elementId?: number | null;
		  }
		| string
		| number
		| Date
		| null
		| undefined;
}

// Type aliases for intermediate objects
type UniqueElement = {
	order: number;
	decimals: number;
};
type UniqueElementsMap = Record<string, UniqueElement>;
type SamplesMap = Record<string, TransformedRowData>;
type LimitsMap = Record<string, number | null>;

export const useElementTableData = ({
	elementList,
	coloringEnabled,
	onToggleColoring,
	editable = false,
	onAddSample,
	gradeId,
	onDeleteSample,
	onCellClicked,
	isSampleTooOld,
}: Props) => {
	const { rowData, chemistryGroupCol } = useMemo(() => {
		// Maps to store unique elements, grouped samples and min/max limits
		const uniqueElements: UniqueElementsMap = {};
		const samplesMap: SamplesMap = {};
		const minLimits: LimitsMap = {};
		const maxLimits: LimitsMap = {};
		const elementHasNegative: Record<string, boolean> = {};

		// Process each sample and group data (only if elementList is not empty)
		if (elementList && elementList.length > 0) {
			elementList.forEach((sample) => {
				const sampleId = `${sample.SampleTypeName || "?"}${
					sample.SampleSequence ?? ""
				}`;
				const elementName = sample.ElementName;
				if (!elementName) return;

				// Register unique element info
				if (!uniqueElements[elementName]) {
					uniqueElements[elementName] = {
						order: sample.Order ?? Infinity,
						decimals: sample.Decimals ?? 4,
					};
					minLimits[elementName] = null;
					maxLimits[elementName] = null;
					elementHasNegative[elementName] = false;
				}

				if (sample.Value !== null && sample.Value < 0) {
					elementHasNegative[elementName] = true;
				}

				// Update min and max limits
				if (sample.MinValue !== null) {
					if (
						minLimits[elementName] === null ||
						sample.MinValue < minLimits[elementName]!
					) {
						minLimits[elementName] = sample.MinValue;
					}
				}
				if (sample.MaxValue !== null) {
					if (
						maxLimits[elementName] === null ||
						sample.MaxValue > maxLimits[elementName]!
					) {
						maxLimits[elementName] = sample.MaxValue;
					}
				}

				// Group sample by sampleId
				if (!samplesMap[sampleId]) {
					samplesMap[sampleId] = {
						id: sampleId,
						SampleTypeName: sample.SampleTypeName || "",
						SampleSequence: sample.SampleSequence ?? null,
						Reception: sample.Reception || null,
						ChemistrySampleId: sample.ChemistrySampleId, // Store at row level
					};
				}
				samplesMap[sampleId][elementName] = {
					value: sample.Value,
					status: sample.Status,
					minValue: sample.MinValue,
					maxValue: sample.MaxValue,
					decimals: uniqueElements[elementName].decimals,
					chemistrySampleId: sample.ChemistrySampleId,
					elementId: sample.ElementId,
				};
			});
		}

		// Order elements by 'order' property and then alphabetically
		// If elementList was empty, uniqueElements is empty, sortedElements is []
		const sortedElements = Object.keys(uniqueElements).sort((a, b) => {
			const orderA = uniqueElements[a].order;
			const orderB = uniqueElements[b].order;
			return orderA !== orderB ? orderA - orderB : a.localeCompare(b);
		});

		// --- Define static columns here (always defined) ---
		const staticColumns: ColDef[] = [
			{
				headerName: "Sample",
				field: "id",
				valueGetter: (params: ValueGetterParams<TransformedRowData>) => {
					if (!params.data) return "";
					return params.data.id;
				},
				minWidth: 69,
				maxWidth: 69,
				flex: 1,
				onCellClicked,
			},
			{
				headerName: "Time",
				field: "Reception",
				minWidth: 62,
				maxWidth: 62,
				resizable: true,
				valueGetter: (params: ValueGetterParams<TransformedRowData>) => {
					if (
						!params.data ||
						params.data.id === "MIN" ||
						params.data.id === "MAX"
					)
						return "";
					return params.data.Reception
						? moment(params.data.Reception).format("HH:mm")
						: "";
				},
				flex: 1,
				onCellClicked,
			},
		];

		// Dynamic column definitions for each element
		const dynamicColumns: ColDef[] = sortedElements.map((elementName) => {
			const defaultDecimals = uniqueElements[elementName].decimals;
			const hasNegativeValue =
				elementHasNegative[elementName] ||
				(minLimits[elementName] !== null && minLimits[elementName]! < 0) ||
				(maxLimits[elementName] !== null && maxLimits[elementName]! < 0);
			const extraWidthForSign = hasNegativeValue ? 7 : 0;
			const minWidth = 43 + defaultDecimals * 7 + extraWidthForSign;
			return {
				headerName: elementName,
				field: elementName,
				minWidth,
				flex: 1,
				editable: editable
					? (params: any) => {
							// Don't allow editing MIN/MAX rows
							if (
								!params.data ||
								params.data.id === "MIN" ||
								params.data.id === "MAX"
							) {
								return false;
							}
							// Don't allow editing if sample is too old
							if (isSampleTooOld && params.data.Reception) {
								return !isSampleTooOld(params.data.Reception);
							}
							return true;
						}
					: false,
				cellEditor: editable ? "agNumberCellEditor" : undefined,
				cellEditorParams: editable
					? {
							min: null,
							max: null,
							precision: defaultDecimals,
							step: Math.pow(10, -defaultDecimals),
						}
					: undefined,
				valueGetter: (params: ValueGetterParams<TransformedRowData>) => {
					if (!params.data) return null;
					const colData = params.data[elementName];
					if (colData && typeof colData === "object") {
						return (colData as { value: number | null }).value;
					}
					return null;
				},
				valueSetter: editable
					? (params: any) => {
							// This will be handled by onCellValueChanged in the parent component
							// We just need to update the local data structure
							if (!params.data) return false;
							const colData = params.data[elementName];
							if (colData && typeof colData === "object") {
								colData.value = params.newValue;
								return true;
							}
							return false;
						}
					: undefined,
				valueFormatter: (params: ValueFormatterParams<TransformedRowData>) => {
					if (params.value === null || params.value === undefined) return "-";
					let decimals = defaultDecimals;
					if (params.data) {
						const colData = params.data[elementName];
						if (colData && typeof colData === "object") {
							decimals = (colData as any).decimals ?? defaultDecimals;
						}
					}
					return typeof params.value === "number"
						? params.value.toFixed(decimals)
						: "-";
				},
				cellClassRules: {
					"cell-below-min": (
						params: CellClassParams<TransformedRowData>,
					): boolean => {
						if (!coloringEnabled) return false;
						if (
							params.rowIndex !== params.api.getDisplayedRowCount() - 3 ||
							!params.data ||
							params.data.id === "MIN" ||
							params.data.id === "MAX"
						)
							return false;
						const colData = params.data[elementName];
						return !!(
							colData &&
							typeof colData === "object" &&
							(colData as any).status === "Below"
						);
					},
					"cell-above-max": (
						params: CellClassParams<TransformedRowData>,
					): boolean => {
						if (!coloringEnabled) return false;
						if (
							params.rowIndex !== params.api.getDisplayedRowCount() - 3 ||
							!params.data ||
							params.data.id === "MIN" ||
							params.data.id === "MAX"
						)
							return false;
						const colData = params.data[elementName];
						return !!(
							colData &&
							typeof colData === "object" &&
							(colData as any).status === "Above"
						);
					},
				},
				type: "numericColumn",
			};
		});

		// Actions column for delete icon (only if onDeleteSample is provided)
		const actionsColumn: ColDef[] = onDeleteSample
			? [
					{
						field: "actions",
						headerName: " ",
						flex: 1,
						minWidth: 40,
						maxWidth: 40,
						editable: false,
						cellRendererParams: {
							onDeleteSample,
						},
						cellRenderer: (
							params: ICellRendererParams<TransformedRowData> & {
								onDeleteSample?: (id: number) => void;
							},
						) => {
							if (
								!params.data ||
								params.data.id === "MIN" ||
								params.data.id === "MAX"
							) {
								return null;
							}
							const chemistrySampleId = params.data.ChemistrySampleId;
							if (!chemistrySampleId) {
								return null;
							}
							const handleDelete = () => {
								if (params.onDeleteSample && chemistrySampleId) {
									params.onDeleteSample(chemistrySampleId);
								}
							};
							return (
								<div className="delete-icon-container">
									<IconButton
										color="secondary"
										size="small"
										onClick={handleDelete}
										title="Delete"
										sx={{
											padding: "4px",
										}}
									>
										<DeleteIcon
											style={{ cursor: "pointer" }}
											fontSize="small"
										/>
									</IconButton>
								</div>
							);
						},
					},
				]
			: [];

		// Create the "Chemistry" ColGroupDef (always created)
		// Only show "Chemistry" title if there are elements
		const chemistryGroup: ColGroupDef = {
			headerName: sortedElements.length > 0 ? "Chemistry" : "",
			children: [...staticColumns, ...dynamicColumns, ...actionsColumn], // dynamicColumns is [] if no elements
			headerGroupComponent: onAddSample
				? (params: any) => (
						<CustomHeaderGroup
							{...params}
							onAddSample={onAddSample}
							gradeId={gradeId}
							hasElements={sortedElements.length > 0}
						/>
					)
				: undefined,
		};

		// Build final row data from grouped samples
		// If elementList was empty, samplesMap is empty, finalRowData is []
		const finalRowData: TransformedRowData[] = Object.values(samplesMap);

		// Add MIN and MAX rows only if there are elements
		if (sortedElements.length > 0) {
			// Create MIN and MAX rows
			const minRow: TransformedRowData = {
				id: "MIN",
				SampleTypeName: "MIN",
				SampleSequence: null,
				Reception: null,
			};
			const maxRow: TransformedRowData = {
				id: "MAX",
				SampleTypeName: "MAX",
				SampleSequence: null,
				Reception: null,
			};

			// Populate MIN and MAX rows with respective limits
			sortedElements.forEach((elementName) => {
				const defaultDecimals = uniqueElements[elementName].decimals;
				minRow[elementName] = {
					value: minLimits[elementName],
					decimals: defaultDecimals,
				};
				maxRow[elementName] = {
					value: maxLimits[elementName],
					decimals: defaultDecimals,
				};
			});

			finalRowData.push(minRow, maxRow);
		}

		// Always return a valid group definition and the (potentially empty) row data
		return { rowData: finalRowData, chemistryGroupCol: chemistryGroup };
	}, [
		elementList,
		coloringEnabled,
		editable,
		onAddSample,
		gradeId,
		onDeleteSample,
		onCellClicked,
		isSampleTooOld,
	]);

	// Row style for MIN/MAX rows
	const getRowStyle = (params: RowClassParams): RowStyle =>
		params.data && (params.data.id === "MIN" || params.data.id === "MAX")
			? { backgroundColor: "#f0f0f0" }
			: {};

	const columns: (ColDef | ColGroupDef)[] = useMemo(
		() => (chemistryGroupCol ? [chemistryGroupCol] : []),
		[chemistryGroupCol], // Dependency on the object returned by the main useMemo
	);

	// Default column configuration
	const defaultColDef: ColDef = useMemo(
		() => ({
			sortable: false,
			editable: false,
			suppressMenu: true,
			flex: 1,
		}),
		[],
	);

	// Status bar configuration
	const frameworkComponents = useMemo(
		() => ({
			statusBarReference: StatusBarReference,
		}),
		[],
	);

	const statusBar = useMemo(
		() => ({
			statusPanels: [
				{
					statusPanel: "statusBarReference",
					align: "left" as const,
					statusPanelParams: {
						coloringEnabled,
						onToggleColoring,
					},
				},
			],
		}),
		[coloringEnabled, onToggleColoring],
	);

	return {
		columns,
		defaultColDef,
		rowData,
		getRowStyle,
		frameworkComponents,
		statusBar,
	};
};
